import React, { useEffect, useState } from "react";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  Drawer,
  Toolbar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
} from "@mui/material";
import { motion } from "framer-motion";
import LogoutIcon from "@mui/icons-material/Logout";
import { useAtom, useSetAtom } from "jotai";
import { fetchSidebarUserAtom, sidebarUserAtom } from "../../state/atoms/sidebarUserAtom";


interface MenuItem {
  id: string;
  label: string;
  icon: React.ReactNode;
  path: string;
}

interface SidebarProps {
  menuItems: MenuItem[];
  bottomItems?: MenuItem[];
  onLogout: () => void;
  logoSrc?: string;
}

const getAvatarProps = (accountType: string): { label: string; bgColor: string } => {
  switch (accountType) {
    case "personal":
      return { label: "Personal", bgColor: "#2E7D32" };
    case "CLIENT":
      return { label: "Client", bgColor: "#1976D2" };
    case "EYEVIATION":
      return { label: "Eyeviation", bgColor: "#9C27B0" };
    case "DISTRIBUTOR":
      return { label: "Distributor", bgColor: "#F57C00" };
    default:
      return { label: "User", bgColor: "#424242" };
  }
};

const Sidebar: React.FC<SidebarProps> = ({ menuItems, bottomItems, onLogout, logoSrc }) => {
  const drawerWidth = 240;
  const [drawerOpen, setDrawerOpen] = useState(true);
  const location = useLocation();

  const [user] = useAtom(sidebarUserAtom);
  const fetchUser = useSetAtom(fetchSidebarUserAtom);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  
  return (
    <Drawer
      id="sidebar-drawer"
      variant="permanent"
      open={drawerOpen}
      sx={{
        width: drawerOpen ? drawerWidth : 72,
        flexShrink: 0,
        "& .MuiDrawer-paper": {
          width: drawerOpen ? drawerWidth : 72,
          backgroundColor: "#071019",
          color: "white",
          transition: "width 0.3s ease",
          overflowX: "hidden",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "space-between",
          borderRight: "solid 1px #51585E",        
        },
      }}
    >
      <Toolbar sx={{ width: "100%", display: "flex", justifyContent: "center", paddingY: 1 }}>
        {logoSrc && (
          <Box
            component={motion.img}
            src={logoSrc}
            alt="Company Logo"
            sx={{ height: "70px", width: "auto", cursor: "pointer" }}
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            whileHover={{ scale: 1.1, filter: "brightness(1.3)" }}
            onClick={() => setDrawerOpen(!drawerOpen)}
          />
        )}
      </Toolbar>

      {drawerOpen && user && (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            py: 3,
          }}
        >


        
        </Box>
      )}

      <List sx={{ width: "100%", paddingTop: 2, flexGrow: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "5px", }}>
        {menuItems.map((item) => (
          <ListItem
            key={item.id}
            id={`${item.id}-nav`}
            component={RouterLink}
            to={item.path}
            sx={{
              justifyContent: "center",
              backgroundColor: location.pathname.includes(item.path)
                ? "#202830"
                : "transparent",
              "&:hover": { backgroundColor: "#141C25" },
              width: "90%",
              borderRadius: "5px",
              height: "55px",
            }}
          >
            <ListItemIcon sx={{ minWidth: "auto", color: location.pathname.includes (item.path) ? "#1DB0BF" : "#B5B7BA" }}>{item.icon}</ListItemIcon>
            {drawerOpen && (
              <ListItemText primary={item.label} primaryTypographyProps={{ sx: { color: location.pathname.includes (item.path) ? "#1DB0BF" : "#B5B7BA", fontWeight: "bold", marginLeft: 2 } }}/>
            )}
          </ListItem>
        ))}
      </List>

  <Box sx={{ height: "1px", width: "90%", backgroundColor: "rgba(255,255,255,0.3)", my: 1 }} />

  <Box sx={{ width: "100%", display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center", }}>

      {bottomItems && (
  <List sx={{ width: "90%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "5px", mb: 1, marginBottom: "0", padding: "0"  }}>
    {bottomItems.map((item) => (
      <ListItem
        key={item.id}
        id={`${item.id}-nav`}
        component={RouterLink}
        to={item.path}
        sx={{
          justifyContent: "center",
          backgroundColor: location.pathname.includes(item.path)
            ? "#202830"
            : "transparent",
          "&:hover": { backgroundColor: "#141C25" },
          borderRadius: "5px",
          height: "50px",
        }}
      >
        <ListItemIcon sx={{ minWidth: "auto", color: location.pathname.includes(item.path) ? "#1DB0BF" : "#B5B7BA" }}>
          {item.icon}
        </ListItemIcon>
        {drawerOpen && (
          <ListItemText
            primary={item.label}
            primaryTypographyProps={{
              sx: {
                color: location.pathname.includes(item.path) ? "#1DB0BF" : "#B5B7BA",
                fontWeight: "bold",
                marginLeft: 2,
                  },
                }}
              />
            )}
          </ListItem>
        ))}

      </List>
      )}

      <List sx={{ width: "90%", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "5px", mb: 1, marginTop: "0", padding: "0"  }}>
        <ListItem
          id="logout-nav"
          onClick={onLogout}
          sx={{
            justifyContent: "center",
            "&:hover": { backgroundColor: "#141C25", cursor: "pointer" },
            borderRadius: "5px",
            height: "55px",
            fontWeight: "bold",
          }}
          title="Logout"
        >
          <ListItemIcon sx={{ minWidth: "auto" }}>
            <LogoutIcon id="logout-icon" sx={{ color: "#B5B7BA" ,transform: 'scaleX(-1)' }} />
          </ListItemIcon>
          {drawerOpen && (
            <ListItemText primary="Logout" sx={{ color: "#B5B7BA", marginLeft: 2, fontWeight: "bold", }} />
          )}
        </ListItem>

      </List>
      </Box>


    
    </Drawer>
  );
};

export default Sidebar;